/**
 * Cold Storage data layer - checkbox + description. Fetches facilities when layer is on.
 * Uses useDataLayer for MapController; fetches data and calls setColdStorageData when active.
 */
import React, { useState, useEffect } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import { useDataLayer } from '../hooks/useDataLayer';
import { useApi } from '../../../api/api';

export default function ColdStorageDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { isReady, isActive, setActive, mapController } = useDataLayer(props);
  const { getColdStorage } = useApi();
  const [loading, setLoading] = useState(false);
  const [facilityCount, setFacilityCount] = useState<number | null>(null);

  useEffect(() => {
    if (!mapController || !mapController.isReady() || !isActive) {
      if (!isActive) setFacilityCount(null);
      return;
    }
    let cancelled = false;
    const fetchData = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await getColdStorage();
        const coldStorageData = response?.data ?? [];
        if (!cancelled) {
          mapController.setColdStorageData(
            Array.isArray(coldStorageData) ? coldStorageData : []
          );
          setFacilityCount(
            Array.isArray(coldStorageData) ? coldStorageData.length : 0
          );
        }
      } catch (err: unknown) {
        console.error('Error fetching cold storage data:', err);
        if (!cancelled) {
          mapController.setColdStorageData([]);
          setFacilityCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isActive, mapController, getColdStorage]);

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">Cold Storage Facilities</span>
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          marginBottom: '18px',
          fontStyle: 'italic',
        }}
      >
        Seafood cold storage and processing facilities in the region
      </div>
      <div className="form-group">
        <label
          className="checkbox-label"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setActive(e.target.checked)
            }
            disabled={!isReady}
            style={{ marginRight: '8px' }}
          />
          <span>Show Cold Storage Facilities</span>
          {loading && (
            <span
              style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}
            >
              (Loading...)
            </span>
          )}
        </label>
      </div>
      {isActive && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f0f9ff',
            borderRadius: '6px',
            fontSize: '12px',
            border: '1px solid #bae6fd',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: '8px',
              color: '#0369a1',
            }}
          >
            Cold Storage Facilities Displayed
          </div>
          <div style={{ fontSize: '11px', color: '#555' }}>
            Click on a marker to see facility details including company name,
            address, and contact information.
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: '10px',
              fontSize: '11px',
              color: '#666',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                marginRight: '6px',
                border: '2px solid white',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.2)',
              }}
            />
            <span>Cold Storage Location</span>
          </div>
        </div>
      )}
      {isActive && !loading && facilityCount === 0 && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#fef2f2',
            borderRadius: '6px',
            fontSize: '12px',
            border: '1px solid #fecaca',
            color: '#dc2626',
          }}
        >
          No cold storage facilities found. Please check your backend connection.
        </div>
      )}
    </div>
  );
}
